import {
  listAssets,
  patchProductionAsset,
  patchSceneStoryboard,
  registerProductionAsset,
  uploadProductionAsset,
  type ProductionAsset,
} from '@/api/studio-production'
import { assetMediaUrl } from '@/features/studio/lib/media-display'

function mediaUrlMatchKey(raw: string): string {
  const url = assetMediaUrl({ media_url: raw, url: raw }).trim()
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) {
    try {
      const parsed = new URL(url)
      parsed.hash = ''
      return parsed.href.replace(/\/$/, '')
    } catch {
      return url.replace(/\/$/, '')
    }
  }
  return url.replace(/\/$/, '')
}

function resolveAssetsForUrls(urls: string[], catalog: ProductionAsset[]): ProductionAsset[] {
  const byKey = new Map<string, ProductionAsset>()
  for (const asset of catalog) {
    const key = mediaUrlMatchKey(assetMediaUrl(asset))
    if (key) byKey.set(key, asset)
  }
  const out: ProductionAsset[] = []
  const seen = new Set<string>()
  for (const raw of urls) {
    const key = mediaUrlMatchKey(raw)
    if (!key || seen.has(key)) continue
    const asset = byKey.get(key)
    if (!asset) continue
    seen.add(key)
    out.push(asset)
  }
  return out
}

export const SCENE_REF_TAG_PREFIX = 'scene-ref:'

export function sceneReferenceAssetTag(scopeKey: string): string {
  return `${SCENE_REF_TAG_PREFIX}${scopeKey}`
}

export function isSceneReferenceAsset(asset: ProductionAsset, scopeKey: string): boolean {
  const tag = sceneReferenceAssetTag(scopeKey)
  return (asset.tags ?? []).includes(tag)
}

export async function listSceneReferenceAssets(
  productionId: string,
  scopeKey: string,
): Promise<ProductionAsset[]> {
  const res = await listAssets(productionId, { tag: sceneReferenceAssetTag(scopeKey) })
  return res.items ?? []
}

export function collectSceneReferenceUrls(
  assets: ProductionAsset[] | null | undefined,
  legacyStoryboardUrl?: string,
): string[] {
  const urls = (assets ?? []).map((a) => assetMediaUrl(a)).filter((u): u is string => !!u?.trim())
  const legacy = legacyStoryboardUrl?.trim()
  if (legacy && !urls.includes(legacy)) {
    return [legacy, ...urls]
  }
  return urls.length > 0 ? urls : legacy ? [legacy] : []
}

export async function syncSceneStoryboardCover(
  productionId: string,
  scriptId: number,
  episodeNo: number,
  sceneNo: number,
  scopeKey: string,
  currentStoryboardUrl?: string,
): Promise<string> {
  const assets = await listSceneReferenceAssets(productionId, scopeKey)
  const cover = collectSceneReferenceUrls(assets, currentStoryboardUrl)[0] ?? ''
  const prev = currentStoryboardUrl?.trim() ?? ''
  if (cover !== prev) {
    await patchSceneStoryboard(productionId, scriptId, episodeNo, sceneNo, cover)
  }
  return cover
}

export async function uploadSceneReferenceAsset(
  productionId: string,
  scopeKey: string,
  sceneNo: number,
  file: File,
): Promise<ProductionAsset> {
  return uploadProductionAsset(productionId, file, {
    kind: 'storyboard',
    name: `S${String(sceneNo).padStart(2, '0')}-ref`,
    tags: [sceneReferenceAssetTag(scopeKey), 'scene-board'],
    shot_scene_key: scopeKey,
  })
}

export async function linkAssetsToSceneReference(
  productionId: string,
  scopeKey: string,
  assetUrls: string[],
  catalogHint?: ProductionAsset[],
): Promise<void> {
  if (assetUrls.length === 0) return
  const tag = sceneReferenceAssetTag(scopeKey)
  const catalog = catalogHint?.length
    ? catalogHint
    : ((await listAssets(productionId)).items ?? [])
  const targets = resolveAssetsForUrls(assetUrls, catalog)
  await Promise.all(
    targets.map(async (asset) => {
      const url = assetMediaUrl(asset)
      if (!url) return
      if ((asset.tags ?? []).includes(tag)) return
      if (asset.registered === false || asset.id.startsWith('canvas:')) {
        await registerProductionAsset(productionId, {
          url,
          name: asset.name,
          kind: asset.kind || 'storyboard',
          source: asset.source || 'canvas',
          canvas_node_id: asset.canvas_node_id,
          tags: [...new Set([...(asset.tags ?? []), tag, 'scene-board'])],
          shot_scene_key: scopeKey,
        })
        return
      }
      const id = asset.public_id ?? asset.id
      const tags = [...new Set([...(asset.tags ?? []), tag])]
      await patchProductionAsset(productionId, id, { tags })
    }),
  )
}

export async function unlinkSceneReferenceAsset(
  productionId: string,
  scopeKey: string,
  asset: ProductionAsset,
): Promise<void> {
  const tag = sceneReferenceAssetTag(scopeKey)
  const id = asset.public_id ?? asset.id
  const tags = (asset.tags ?? []).filter((t) => t !== tag)
  await patchProductionAsset(productionId, id, { tags })
}
